import { useEffect, useState } from 'react';
import { lade, gruenden, beitreten, verlassen, aufloesen, neuerCode, umbenennen, shopUmbenennen, entfernen } from '../services/netzwerk';
import { getStoredUser } from '../services/apiService';
import NetzwerkUebersicht from './NetzwerkUebersicht';

/**
 * 🏪 Einstellungen → Netzwerk.
 * Ohne Netzwerk: gründen (Name + eigener Shop-Name) oder mit Einladungscode beitreten.
 * Mitglied: sieht Netzwerk-Name, eigenen Shop-Namen (änderbar), was der Inhaber sieht, kann austreten.
 * Inhaber: Name, Einladungscode (neu erzeugen), Shops entfernen, Übersicht (nur Zahlen), Netzwerk auflösen.
 */
export default function NetzwerkDialog({ t, colors, onClose }) {
  const [stand, setStand] = useState(null);
  const [laedt, setLaedt] = useState(true);
  const [modus, setModus] = useState(null); // null | 'gruenden' | 'beitreten'
  const [name, setName] = useState('');
  const [shop, setShop] = useState(() => { try { return (getStoredUser() || {}).name || ''; } catch { return ''; } });
  const [code, setCode] = useState('');
  const [fehler, setFehler] = useState('');
  const [busy, setBusy] = useState(false);
  const [kopiert, setKopiert] = useState(false);
  const dark = colors?.darkMode;
  const f = { text: dark ? '#fff' : '#23272f', sub: dark ? '#aaa' : '#666', rand: dark ? '#444' : '#ddd', feld: dark ? '#1b1f27' : '#fff', zeile: dark ? '#2d333b' : '#f5f6f8' };

  useEffect(() => {
    lade().then(setStand).catch(() => setFehler(t('staffs.offline'))).finally(() => setLaedt(false));
  }, [t]);

  const meldung = (e) => {
    const code = e && e.data && e.data.error;
    if (e && e.status === 404) return t('network.codeUnknown');
    if (e && e.status === 409) return t('network.alreadyIn');
    if (e && e.status === 429) return t('staffs.locked');
    if (code === 'code') return t('network.codeFormat');
    if (code === 'name' || code === 'shopName') return t('network.nameMissing');
    if (code === 'voll') return t('network.full');
    return t('staffs.offline');
  };
  const tu = async (fn) => {
    setBusy(true); setFehler('');
    try { const s = await fn(); if (s !== undefined) setStand(s); return true; } catch (e) { setFehler(meldung(e)); return false; } finally { setBusy(false); }
  };

  const feld = { width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${f.rand}`, fontSize: 15, background: f.feld, color: f.text, boxSizing: 'border-box', marginTop: 8 };
  const knopf = (primary, farbe) => ({
    width: '100%', padding: '12px', borderRadius: 10, marginTop: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer',
    border: primary ? 'none' : `1px solid ${f.rand}`, background: primary ? (farbe || '#1976d2') : 'transparent', color: primary ? '#fff' : (farbe || f.text),
  });
  const klein = { padding: '6px 10px', borderRadius: 8, border: `1px solid ${f.rand}`, background: 'transparent', color: f.text, fontSize: 13, cursor: 'pointer' };
  const hinweis = (txt) => <div style={{ fontSize: 13, color: f.sub, marginTop: 8, lineHeight: 1.5 }}>{txt}</div>;
  const n = stand && stand.netzwerk;

  const kopieren = async () => {
    try { await navigator.clipboard.writeText(n.code); setKopiert(true); setTimeout(() => setKopiert(false), 1500); } catch { /* */ }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 7600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-testid="network-dialog" style={{ background: dark ? '#23272f' : '#fff', color: f.text, borderRadius: 14, width: '100%', maxWidth: 560,
        maxHeight: '92vh', overflowY: 'auto', padding: 18, boxShadow: '0 10px 40px rgba(0,0,0,0.4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <strong style={{ fontSize: 17 }}>🏪 {n ? n.name : t('network.title')}</strong>
          <button type="button" data-testid="network-close" onClick={onClose} aria-label={t('common.close')}
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: f.text }}>×</button>
        </div>

        {laedt && <div style={{ marginTop: 12, color: f.sub }}>⏳</div>}

        {/* ── kein Netzwerk ── */}
        {!laedt && !n && !modus && (
          <>
            {hinweis(t('network.explain'))}
            <button type="button" data-testid="network-start-join" style={knopf(true)} onClick={() => { setModus('beitreten'); setFehler(''); }}>{t('network.joinButton')}</button>
            <button type="button" data-testid="network-start-create" style={knopf(false)} onClick={() => { setModus('gruenden'); setFehler(''); }}>{t('network.createButton')}</button>
          </>
        )}
        {!laedt && !n && modus === 'beitreten' && (
          <>
            {hinweis(t('network.joinExplain'))}
            <input data-testid="network-code" value={code} maxLength={9} autoCapitalize="characters" autoComplete="off" spellCheck={false}
              placeholder={t('network.codePlaceholder')} onChange={(e) => setCode(e.target.value.toUpperCase())}
              style={{ ...feld, fontFamily: 'monospace', letterSpacing: '0.2em', textAlign: 'center', fontSize: 20 }} />
            <input data-testid="network-shopname" value={shop} maxLength={60} placeholder={t('network.shopNamePlaceholder')} onChange={(e) => setShop(e.target.value)} style={feld} />
            <div data-testid="network-consent" style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: f.zeile, fontSize: 13, lineHeight: 1.5 }}>
              <strong>{t('network.ownerSeesTitle')}</strong><br />{t('network.ownerSees')}<br /><strong>{t('network.ownerNeverTitle')}</strong> {t('network.ownerNever')}
            </div>
            <button type="button" data-testid="network-join" disabled={busy} style={knopf(true)} onClick={() => tu(() => beitreten(code, shop.trim()))}>{t('network.joinConfirm')}</button>
            <button type="button" style={knopf(false)} onClick={() => setModus(null)}>{t('common.cancel')}</button>
          </>
        )}
        {!laedt && !n && modus === 'gruenden' && (
          <>
            {hinweis(t('network.createExplain'))}
            <input data-testid="network-name" value={name} maxLength={60} placeholder={t('network.namePlaceholder')} onChange={(e) => setName(e.target.value)} style={feld} />
            <input data-testid="network-shopname" value={shop} maxLength={60} placeholder={t('network.shopNamePlaceholder')} onChange={(e) => setShop(e.target.value)} style={feld} />
            <button type="button" data-testid="network-create" disabled={busy} style={knopf(true)} onClick={() => tu(() => gruenden(name.trim(), shop.trim()))}>{t('network.createConfirm')}</button>
            <button type="button" style={knopf(false)} onClick={() => setModus(null)}>{t('common.cancel')}</button>
          </>
        )}

        {/* ── Mitglied ── */}
        {n && stand.rolle === 'mitglied' && (
          <>
            {hinweis(t('network.memberExplain', { n: n.anzahl }))}
            <div style={{ fontSize: 13, color: f.sub, marginTop: 12 }}>{t('network.yourShop')}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input data-testid="network-shopname" value={shop || n.shopName} maxLength={60} onChange={(e) => setShop(e.target.value)} style={{ ...feld, flex: 1 }} />
              <button type="button" data-testid="network-shopname-save" disabled={busy} style={{ ...klein, marginTop: 8 }} onClick={() => tu(() => shopUmbenennen((shop || n.shopName).trim()))}>{t('common.save')}</button>
            </div>
            <div data-testid="network-consent" style={{ marginTop: 12, padding: '8px 10px', borderRadius: 8, background: f.zeile, fontSize: 13, lineHeight: 1.5 }}>
              <strong>{t('network.ownerSeesTitle')}</strong><br />{t('network.ownerSees')}<br /><strong>{t('network.ownerNeverTitle')}</strong> {t('network.ownerNever')}
            </div>
            <button type="button" data-testid="network-leave" disabled={busy} style={knopf(false, '#e5484d')}
              onClick={() => { if (window.confirm(t('network.leaveConfirm'))) tu(async () => { await verlassen(); return { rolle: null, netzwerk: null }; }); }}>
              {t('network.leave')}
            </button>
          </>
        )}

        {/* ── Inhaber ── */}
        {n && stand.rolle === 'inhaber' && (
          <>
            <div style={{ fontSize: 13, color: f.sub, marginTop: 10 }}>{t('network.inviteCode')}</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <code data-testid="network-code-show" style={{ fontSize: 24, letterSpacing: '0.2em', padding: '6px 10px', borderRadius: 8, background: f.zeile, flex: 1, textAlign: 'center' }}>{n.code}</code>
              <button type="button" style={klein} onClick={kopieren}>{kopiert ? '✓' : t('network.copy')}</button>
              <button type="button" data-testid="network-newcode" disabled={busy} style={klein} onClick={() => { if (window.confirm(t('network.newCodeConfirm'))) tu(neuerCode); }}>{t('network.newCode')}</button>
            </div>
            {hinweis(t('network.inviteExplain'))}

            <div style={{ fontSize: 13, color: f.sub, marginTop: 14 }}>{t('network.members', { n: n.anzahl })}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
              {(n.mitglieder || []).map((m) => (
                <div key={m.id} data-testid="network-member" style={{ display: 'flex', alignItems: 'center', gap: 6, background: f.zeile, borderRadius: 8, padding: '6px 10px', fontSize: 14 }}>
                  <span style={{ flex: 1, fontWeight: 600 }}>{m.shopName}{m.inhaber && <span style={{ color: f.sub, fontWeight: 400 }}> · {t('network.you')}</span>}</span>
                  {!m.inhaber && (
                    <button type="button" data-testid="network-member-remove" disabled={busy} style={{ ...klein, color: '#e5484d' }}
                      onClick={() => { if (window.confirm(t('network.removeConfirm', { name: m.shopName }))) tu(() => entfernen(m.id)); }}>{t('staffs.remove')}</button>
                  )}
                </div>
              ))}
            </div>

            <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${f.rand}` }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>📊 {t('network.overviewTitle')}</div>
              <NetzwerkUebersicht t={t} darkMode={dark} kompakt refreshKey={n.anzahl} />
            </div>

            <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${f.rand}` }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <input data-testid="network-name" value={name || n.name} maxLength={60} onChange={(e) => setName(e.target.value)} style={{ ...feld, flex: 1, marginTop: 0 }} />
                <button type="button" disabled={busy} style={klein} onClick={() => tu(() => umbenennen((name || n.name).trim()))}>{t('common.save')}</button>
              </div>
              <button type="button" data-testid="network-dissolve" disabled={busy} style={knopf(false, '#e5484d')}
                onClick={() => { if (window.confirm(t('network.dissolveConfirm'))) tu(async () => { await aufloesen(); return { rolle: null, netzwerk: null }; }); }}>
                {t('network.dissolve')}
              </button>
            </div>
          </>
        )}

        {fehler && <div data-testid="network-error" style={{ color: '#e5484d', fontSize: 13, marginTop: 10 }}>⚠️ {fehler}</div>}
      </div>
    </div>
  );
}
